import {Injectable, inject, signal, computed} from '@angular/core';
import { ReplaySubject, of } from 'rxjs';
import {map, tap, first} from 'rxjs/operators';
import { CmsApiService } from './api/cms-api.service';
import { LanguageService } from './language.service';
import {catchErrorAndReturn} from "ngx-unificator/rxjs";
import {CurrencyList$} from "../helpers/user/user-fields.data";

interface CurrencyRate {
  from: string;
  to: string;
}
/**
 * Temporary not needed currencies
 */
export const NOT_NEEDED_CURRENCIES = ['RUB'];

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  private _language = inject(LanguageService);
  private _api = inject(CmsApiService);

  public loaded = signal<boolean>(false);
  public currencyList = signal<any[]>([]);
  public cryptoCurrencyList = computed(() => this.currencyList()?.filter(e => !e.fiat));
  public fiatCurrencyList = computed(() => this.currencyList()?.filter(e => e.fiat));

  public currencyList$ = new ReplaySubject<any[]>(1)

  public currentRate = null;
  private _cashedRate = {};


  /**
   * Loads the list of currencies from the platform API and sets the `currencyList` signal.
   * The currency list is mapped to include the currency code, symbol, and whether it is a fiat currency.
   * Any errors during the API call are caught and an empty array is returned.
   */
  public loadCurrencies() {
    return this._api.currencyListSS().pipe(
      map(currencyList => currencyList.data.filter((currency: any) => !NOT_NEEDED_CURRENCIES.includes(currency.code))),
      catchErrorAndReturn([]),
      tap(currencyList => {
        this.currencyList.set(currencyList);
        CurrencyList$.next(this.currencyList());
        this.currencyList$.next(this.currencyList());
        this.loaded.set(true);
      }),
      first(),
    );
  }


  /**
   * Returns symbol of provided currency code
   *
   * @param code
   */
  currencySymbol(code: string): string {
    return (this.currencyList()?.find(currency => currency.code === code) || {}).symbol || '';
  }

  /**
   * Convert amount subunits to units (cents to euros)
   *
   * @param amount
   * @param currencyCode
   */
  subunitsToUnits(amount: number, currencyCode: string): number {
    return amount / ((this.currencyList()?.find(currency => currency.code === currencyCode) || {}).subunits_to_unit || 100);
  }

  /**
   * Convert amount units to subunits (euros to cents)
   *
   * @param amount
   * @param currencyCode
   */
  unitsToSubunits(amount: number, currencyCode: string): number {
    return amount * ((this.currencyList()?.find(currency => currency.code === currencyCode) || {}).subunits_to_unit || 100);
  }

  /**
   * Check if is crypto currency
   */
  isCryptoCurrency(currency: string): boolean {
    return this.cryptoCurrencyList()?.map(e => e.code).includes(currency);
  }

  /**
   * Return amount & currency for exist locale
   * @param value
   * @param currency
   * @param spacer
   */
  transformLocaleCurrency(value: any, currency: string, spacer): any {
    return this._language.rightLocales.rtCurrencies.includes((currency || '').toUpperCase()) &&
    this._language.rightLocales.rtLanguages.includes(this._language.current)
      ? value + spacer + currency
      : currency + spacer + value;
  }

  public getValueCurrencyTo(value: number, currencies: CurrencyRate): ReplaySubject<number> {
    return this._refreshRate(currencies).pipe(
      map(rate => Number(value) * rate)
    ) as ReplaySubject<number>;
  }

  public getValueCurrencyFrom(value: number, currencies: CurrencyRate): ReplaySubject<number> {
    return this._refreshRate(currencies).pipe(
      map(rate => Number(value) / rate)
    ) as ReplaySubject<number>;
  }

  /**
   * Refresh rates currencies
   * @param currencies
   */
  private _refreshRate(currencies: CurrencyRate) {
    const check = JSON.stringify(currencies);
    if (this._cashedRate[check]) {
      this.currentRate = this._cashedRate[check];
      return of(this._cashedRate[check]);
    } else {
      if (currencies.from === currencies.to) {
        this._cashedRate[check] = 1;
        this.currentRate = 1;
        return of(this._cashedRate[check]);
      }
      return this._api.getExchangeRate(currencies).pipe(
        map(response => {
          if (response?.data?.rate) {
            return Number(response.data.rate);
          } else {
            return null;
          }
        }),
        tap(rate => {
          this._cashedRate[check] = rate;
          this.currentRate = rate;
        })
      ) as ReplaySubject<any>;
    }
  }
}
